/**
 * Config statica delle sezioni e KPI di default per la dashboard "KPI Chiave".
 * I codici devono corrispondere a datapoint esistenti nella campagna corrente.
 * In futuro questa config potrà essere sovrascritta da un modello KpiDefinition in DB.
 */

export interface KpiDefinition {
  code: string;
  label: string;
  /** Chiave i18n per la label (es. 'kpiDashboard.kpi.scope1'). Se presente prevale su label. */
  labelKey?: string;
  unit: string;
  /** Variante colore per evidenziare valori critici */
  valueColor?: 'default' | 'success' | 'danger';
  /** Cifre decimali massime per la formattazione (default 2). Necessario per valori molto piccoli. */
  maxFractionDigits?: number;
  /** Moltiplicatore di sola presentazione (es. 1e6 per convertire tCO₂e/EUR in tCO₂e/M€). Non modifica il dato in DB. */
  scale?: number;
  /** Nota testuale opzionale mostrata sotto la label del KPI (es. "con Garanzie d'Origine"). */
  caption?: string;
  /** Chiave i18n per la caption. Se presente prevale su caption. */
  captionKey?: string;
}

export interface KpiSubsectionDef {
  id: string;
  label: string;
  labelKey?: string;
  kpis: KpiDefinition[];
  /** Se true, questa sottosezione viene affiancata alla precedente sulla stessa riga. */
  inlineWithPrevious?: boolean;
}

export interface KpiSectionDef {
  id: string;
  label: string;
  labelKey?: string;
  color: string;
  icon: string;
  /** Sezione piatta: KPI direttamente sotto l'header. */
  kpis?: KpiDefinition[];
  /** Sezione con sottosezioni. In alternativa a kpis. */
  subsections?: KpiSubsectionDef[];
}

// Codici verificati su DB locale (catalogo DataPointTemplate, aggiornato 2026-04-23).
// Solo datapoint ESRS quantitativi; i KPI non ESRS del design originale (eNPS, voluntary attrition,
// ERG participation, % edifici controllati, % casi risolti) sono stati omessi per scelta esplicita.
export const KPI_SECTIONS: KpiSectionDef[] = [
  {
    id: 'climate',
    label: 'Climate & Energy',
    labelKey: 'kpiDashboard.sections.climate',
    color: '#1565c0',
    icon: '🌿',
    subsections: [
      {
        id: 'scope-emissions',
        label: 'Scope 1+2 emissions',
        labelKey: 'kpiDashboard.subsections.scopeEmissions',
        kpis: [
          { code: 'E1-6_07', label: 'Scope 1 emissions', labelKey: 'kpiDashboard.kpi.scope1', unit: 'tCO₂eq' },
          { code: 'E1-6_09', label: 'Scope 2 emissions (location-based)', labelKey: 'kpiDashboard.kpi.scope2LocationBased', unit: 'tCO₂eq' },
        ],
      },
      {
        id: 'carbon-intensity',
        label: 'Carbon Intensity Index',
        labelKey: 'kpiDashboard.subsections.carbonIntensity',
        inlineWithPrevious: true,
        kpis: [
          { code: 'E1-6_30', label: 'Carbon Intensity Index', labelKey: 'kpiDashboard.kpi.carbonIntensityIndex', unit: 't/M€', maxFractionDigits: 2 },
        ],
      },
      {
        id: 'energy',
        label: 'Energy',
        labelKey: 'kpiDashboard.subsections.energy',
        kpis: [
          { code: 'E1-5_01', label: 'Consumo energetico totale', labelKey: 'kpiDashboard.kpi.totalEnergy', unit: 'MWh', caption: 'Sedi + flotta (own operations)', captionKey: 'kpiDashboard.caption.ownOperations' },
          { code: 'E1-5_05', label: 'Energia da fonti rinnovabili', labelKey: 'kpiDashboard.kpi.renewableEnergy', unit: 'MWh', valueColor: 'success', caption: "Con Garanzie d'Origine (GoO)", captionKey: 'kpiDashboard.caption.withGoO' },
          { code: 'E1-6_18', label: 'Copertura GoO Scope 2', labelKey: 'kpiDashboard.kpi.gooCoverage', unit: '%', valueColor: 'success', caption: 'Elettricità edifici da strumenti contrattuali', captionKey: 'kpiDashboard.caption.gooBuildings' },
        ],
      },
    ],
  },
  {
    id: 'ethical',
    label: 'Ethical Conduct',
    labelKey: 'kpiDashboard.sections.ethical',
    color: '#d97706',
    icon: '⚖️',
    subsections: [
      {
        id: 'code-of-conduct',
        label: 'Code of Conduct',
        labelKey: 'kpiDashboard.subsections.codeOfConduct',
        kpis: [
          { code: 'G1-3_07', label: 'Formazione anti-corruzione', labelKey: 'kpiDashboard.kpi.antiCorruptionTraining', unit: '%', valueColor: 'success', caption: 'Funzioni a rischio coperte', captionKey: 'kpiDashboard.caption.functionsAtRisk' },
          { code: 'G1-4_01', label: 'Condanne anti-corruzione', labelKey: 'kpiDashboard.kpi.convictionsAntiCorruption', unit: 'n.', valueColor: 'danger' },
          { code: 'G1-4_02', label: 'Sanzioni anti-corruzione', labelKey: 'kpiDashboard.kpi.finesAntiCorruption', unit: 'EUR', valueColor: 'danger' },
        ],
      },
      // Nota: i KPI G1-4_04 (incidenti confermati) e G1-4_06 (lavoratori disciplinati)
      // del design originale NON esistono nel framework ESRS Omnibus 2025 (presenti solo in
      // ESRS legacy). Sostituiti con G1-4_01 e G1-4_02 che sono i datapoint numerici
      // effettivamente disponibili nella campagna CSRD Omnibus.
      // Le sottosezioni "Whistleblowing" e "Altre Segnalazioni" del design non hanno
      // datapoint ESRS numerici corrispondenti: saranno riattivate con i Custom KPI.
    ],
  },
  {
    id: 'employees',
    label: 'Employee Engagement & Inclusion',
    labelKey: 'kpiDashboard.sections.employees',
    color: '#ad1457',
    icon: '👥',
    kpis: [
      { code: 'S1-13_02', label: 'Partecipazione performance review', labelKey: 'kpiDashboard.kpi.performanceReviewParticipation', unit: '%' },
      { code: 'S1-6_12', label: 'Turnover dipendenti', labelKey: 'kpiDashboard.kpi.employeeTurnover', unit: '%', valueColor: 'danger' },
      { code: 'S1-16_01', label: 'Gender pay gap', labelKey: 'kpiDashboard.kpi.genderPayGap', unit: '%', valueColor: 'danger' },
      { code: 'S1-14_01', label: 'Forza lavoro coperta da H&S', labelKey: 'kpiDashboard.kpi.workforceHealthSafety', unit: '%', valueColor: 'success' },
      { code: 'S1-14_05', label: 'Tasso infortuni sul lavoro', labelKey: 'kpiDashboard.kpi.workAccidentsRate', unit: 'IF', valueColor: 'danger', caption: 'Infortuni registrabili per milione di ore lavorate', captionKey: 'kpiDashboard.caption.recordableAccidents' },
    ],
  },
  // Sezione "Value Chain / Fornitori" rimossa (2026-04-23): l'ESRS Omnibus 2025 non offre
  // KPI numerici significativi su supplier screening ESG (S2 e G1-2 sono tutti testuali).
  // I payment practices G1-6_01/03, pur disponibili, non rappresentano adeguatamente il
  // concetto di "Value Chain ESG Control" del design originale. La sezione tornerà quando
  // introdurremo i Custom KPI per valori manuali (es. % fornitori validati Modefinance).
];

/** Restituisce tutti i codici da richiedere al backend */
export function getAllKpiCodes(): string[] {
  const codes: string[] = [];
  for (const section of KPI_SECTIONS) {
    if (section.kpis) codes.push(...section.kpis.map((k) => k.code));
    if (section.subsections) {
      for (const sub of section.subsections) codes.push(...sub.kpis.map((k) => k.code));
    }
  }
  return codes;
}

/** Restituisce le definizioni di tutti i KPI attraversando sezioni e sottosezioni. */
export function getAllKpiDefinitions(): KpiDefinition[] {
  const defs: KpiDefinition[] = [];
  for (const section of KPI_SECTIONS) {
    if (section.kpis) defs.push(...section.kpis);
    if (section.subsections) {
      for (const sub of section.subsections) defs.push(...sub.kpis);
    }
  }
  return defs;
}
